import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../../services/api";
import { emitFinanceEvent } from "../finance/financeDispatcher";

interface Supplier {
    id: number;
    name: string;
}

interface Bill {
    id: number;
    invoice_no: string;
    date: string;
    total: number;
    paid: number;
    balance: number;
}

interface RecordPaymentModalProps {
    suppliers: Supplier[];
    onClose: () => void;
    onPaid: () => void;
}

export default function RecordPaymentModal({ suppliers, onClose, onPaid }: RecordPaymentModalProps) {
    const [form, setForm] = useState({
        supplier_id: "",
        bill_id: "",
        amount: "",
        receipt_no: "",
        payment_date: new Date().toISOString().slice(0, 10),
        mode: "Cash"
    });

    const [bills, setBills] = useState<Bill[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (form.supplier_id) {
            loadPendingBills(Number(form.supplier_id));
        } else {
            setBills([]);
            setForm(prev => ({ ...prev, bill_id: "", amount: "" }));
        }
    }, [form.supplier_id]);

    const loadPendingBills = async (supplierId: number) => {
        setLoading(true);
        try {
            const res = await apiGet(`/api/manager/supplier/${supplierId}/bills`);
            const allBills = Array.isArray(res) ? res : [];
            // Filter to only show bills with outstanding balance
            const pendingBills = allBills.filter((b: Bill) => (b.balance || 0) > 0);
            setBills(pendingBills);

            if (pendingBills.length === 0) {
                alert("ℹ️ No pending bills for this supplier");
            }
        } catch (e) {
            console.error("Failed to load bills", e);
            setBills([]);
            alert("Failed to load bills for this supplier");
        } finally {
            setLoading(false);
        }
    };

    const selectedBill = bills.find(b => String(b.id) === form.bill_id);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.supplier_id || !form.bill_id || !form.amount || !form.receipt_no) {
            alert("Please fill all required fields");
            return;
        }

        const paymentAmount = Number(form.amount);
        if (paymentAmount <= 0) {
            alert("Payment amount must be greater than 0");
            return;
        }

        if (selectedBill && paymentAmount > selectedBill.balance) {
            alert(`Payment amount cannot exceed bill balance of ₹${selectedBill.balance.toLocaleString()}`);
            return;
        }

        setSaving(true);
        try {
            await apiPost("/api/manager/supplier-payment", {
                purchase_bill_id: Number(form.bill_id),
                amount: paymentAmount,
                receipt_number: form.receipt_no,
                payment_date: form.payment_date,
                mode: form.mode
            });

            alert("✅ Payment recorded successfully");
            
            // Emit Finance Event for Supplier Payment (Expenditure)
            if (paymentAmount > 0) {
                const supplier = suppliers.find(s => String(s.id) === form.supplier_id);
                await emitFinanceEvent({
                    source: "SUPPLIER_PAYMENT",
                    ref_id: String(form.bill_id),
                    debit: 0,
                    credit: paymentAmount,
                    description: `Supplier payment to ${supplier?.name || "Supplier"} for bill #${selectedBill?.invoice_no || form.bill_id}`,
                    meta: {
                        supplier_id: form.supplier_id,
                        bill_id: form.bill_id,
                        method: form.mode,
                        receipt_no: form.receipt_no
                    }
                });
            }
            onPaid();
        } catch (e) {
            console.error("Failed to record payment", e);
            alert("❌ Failed to record payment. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <form
                className="modal-card"
                onSubmit={handleSubmit}
                onClick={(e) => e.stopPropagation()}
            >
                <h3>💸 Record Supplier Payment</h3>

                <div className="form-group">
                    <label>Supplier *</label>
                    <select
                        required
                        value={form.supplier_id}
                        onChange={e => setForm({ ...form, supplier_id: e.target.value, bill_id: "", amount: "" })}
                        disabled={saving}
                    >
                        <option value="">-- Select Supplier --</option>
                        {suppliers.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Bill Number *</label>
                    <select
                        required
                        value={form.bill_id}
                        onChange={e => {
                            setForm({ ...form, bill_id: e.target.value });
                            const bill = bills.find(b => String(b.id) === e.target.value);
                            if (bill) {
                                setForm(prev => ({ ...prev, amount: String(bill.balance) }));
                            }
                        }}
                        disabled={!form.supplier_id || loading || saving}
                    >
                        <option value="">-- Select Bill --</option>
                        {bills.map(b => (
                            <option key={b.id} value={b.id}>
                                {b.invoice_no} ({b.date}) — Due: ₹{b.balance.toLocaleString("en-IN")}
                            </option>
                        ))}
                    </select>
                    {!form.supplier_id && <small className="muted">Select supplier first</small>}
                    {form.supplier_id && loading && <small className="muted">Loading bills...</small>}
                    {form.supplier_id && !loading && bills.length === 0 && (
                        <small className="muted" style={{ color: "#f59e0b" }}>No pending bills</small>
                    )}
                </div>

                <div className="form-group">
                    <label>Payment Amount (₹) *</label>
                    <input
                        type="number"
                        required
                        min="0.01"
                        step="0.01"
                        max={selectedBill?.balance || undefined}
                        value={form.amount}
                        onChange={e => setForm({ ...form, amount: e.target.value })}
                        placeholder="0.00"
                        disabled={saving}
                    />
                    {selectedBill && (
                        <small className="muted">Maximum: ₹{selectedBill.balance.toLocaleString("en-IN")}</small>
                    )}
                </div>

                <div className="form-group">
                    <label>Receipt Number *</label>
                    <input
                        required
                        value={form.receipt_no}
                        onChange={e => setForm({ ...form, receipt_no: e.target.value })}
                        placeholder="e.g. REC-2024-001"
                        disabled={saving}
                    />
                </div>

                <div className="form-group">
                    <label>Payment Date *</label>
                    <input
                        type="date"
                        required
                        value={form.payment_date}
                        onChange={e => setForm({ ...form, payment_date: e.target.value })}
                        disabled={saving}
                    />
                </div>

                <div className="form-group">
                    <label>Payment Mode *</label>
                    <select
                        value={form.mode}
                        onChange={e => setForm({ ...form, mode: e.target.value })}
                        disabled={saving}
                    >
                        <option value="Cash">Cash</option>
                        <option value="UPI">UPI</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Cheque">Cheque</option>
                    </select>
                </div>

                <div className="modal-actions">
                    <button type="button" className="btn-text" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={saving || bills.length === 0}>
                        {saving ? "Recording..." : "💰 Record Payment"}
                    </button>
                </div>
            </form>
        </div>
    );
}
